import {
  AuthProfile,
  clearAuth,
  setMustChangePassword,
  setProfileSession,
  setTokenSession,
} from '../utils/auth';
import { fetchAuth, getApiBaseUrl } from '../utils/fetchAuth';

type LoginResponse = {
  access?: string;
  refresh?: string;
  token?: string;
};

async function parseError(response: Response): Promise<string> {
  try {
    const data = await response.json();
    return data?.detail || data?.message || 'Falha na autenticação.';
  } catch {
    return 'Falha na autenticação.';
  }
}

export async function login(email: string, password: string): Promise<AuthProfile | null> {
  clearAuth();

  const response = await fetch(`${getApiBaseUrl()}auth/login/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, username: email, password }),
  });

  if (!response.ok) {
    throw new Error(await parseError(response));
  }

  const data = (await response.json()) as LoginResponse;
  const token = data.access || data.token;
  if (!token) {
    throw new Error('A resposta de autenticação não devolveu token.');
  }

  setTokenSession(token, email, data.refresh);

  try {
    const profileResponse = await fetchAuth('auth/me/');
    if (!profileResponse.ok) {
      return null;
    }
    const profile = (await profileResponse.json()) as AuthProfile;
    setProfileSession(profile);
    return profile;
  } catch {
    return null;
  }
}

export function logout() {
  clearAuth();
}

/** Collects every message the backend returned, so validator errors stay readable. */
async function parseChangePasswordError(response: Response): Promise<string> {
  try {
    const data = await response.json();
    if (data?.detail) {
      return String(data.detail);
    }
    const messages = Object.values(data || {})
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .filter(Boolean)
      .map(String);
    return messages.join(' ') || 'Não foi possível alterar a palavra-passe.';
  } catch {
    return 'Não foi possível alterar a palavra-passe.';
  }
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const response = await fetchAuth('auth/change-password/', {
    method: 'POST',
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });

  if (!response.ok) {
    throw new Error(await parseChangePasswordError(response));
  }

  // The obligation is settled, so the gate stops redirecting to this screen.
  setMustChangePassword(false);
}
